/**
 * Side-channel mapping an assistant-ui attachment id to the underlying
 * document's `file_type`. assistant-ui's `CompleteAttachment` has no slot for
 * domain metadata, so the two paths that build attachments register it here and
 * `mapAuiAttachmentToBrief` reads it back at render time — letting the chat pill
 * show a spreadsheet (green) instead of a PDF (red). Same module-state pattern
 * as `attachment-client-registry`, keyed by the EXACT aui id string each path
 * produces: `"type:id"` for both composer-added attachments
 * (ComposerAttachmentMenu) and backend-loaded ones (convert-messages), so a
 * document and a non-document sharing the same numeric id never collide.
 *
 * Writes happen synchronously before the render that reads them (an event
 * handler on attach; the async history `load()` on reload), so render-time
 * reads are never stale. Stale leftovers are harmless: only ids present in live
 * aui state are ever looked up, and a re-attach overwrites the entry.
 */

import type { FileType } from "@/api";

const fileTypeByAttachmentId = new Map<string, FileType>();

export function registerAttachmentFileType(
    attachmentId: string,
    fileType: FileType | null | undefined
): void {
    if (fileType == null) {
        fileTypeByAttachmentId.delete(attachmentId);
        return;
    }
    fileTypeByAttachmentId.set(attachmentId, fileType);
}

export function getAttachmentFileType(attachmentId: string): FileType | null {
    return fileTypeByAttachmentId.get(attachmentId) ?? null;
}

/** Test-only reset so suites can't poison each other through module state. */
export function clearAttachmentFileTypeRegistry(): void {
    fileTypeByAttachmentId.clear();
}
